import {
  MagazineCatchUpdateRequestSchema,
  MagazineCatchUpdateResponseSchema,
  MagazineListResponseSchema,
  type MagazineCatchUpdateRequest,
  type MagazineCatchUpdateResponse,
  type MagazineListResponse,
} from '@/types/api.zod';
import apiClient from '@/utils/apiClient';

/**
 * `GET /books/magazines` のレスポンスを Zod schema で検証してから返す。
 *
 * AdminHttpApi / UserHttpApi いずれにも同じ Lambda が公開されているため、
 * admin-web からは認証済み AdminHttpApi (`VITE_API_ENDPOINT`) 経由で呼ぶ。
 * Lambda 側で `latest_release_at` 降順 + 同日優先度ソート済みのため、
 * フロント側で並び替えやフィルタは行わない。
 *
 * `platform` クエリは MR !187 で必須化されている。管理コンソールは Web 環境
 * 限定なのでハードコードで `web` を渡す。
 *
 * @param signal 呼び出し側 (useEffect のアンマウント等) からのキャンセル用 AbortSignal。
 */
export const fetchMagazines = async (signal?: AbortSignal): Promise<MagazineListResponse> => {
  const response = await apiClient.get<unknown>('/books/magazines', {
    params: { platform: 'web' },
    signal,
  });
  return MagazineListResponseSchema.parse(response.data);
};

/**
 * `POST /admin/magazines/{title_name}/catch` (AdminHttpApi, Auth0 JWT 必須) を呼び、
 * 該当雑誌の `magazine_catch` のみを更新する。
 *
 * - リクエスト / レスポンスは生成済み Zod schema (`MagazineCatchUpdateRequest` /
 *   `MagazineCatchUpdateResponse`) で送信前 / 受信後にバリデーションする。
 * - 認証トークンは `apiClient` の interceptor が自動付与する。
 * - 404 / 400 / 401 / 403 / 500 の振り分けは呼び出し側 (モーダル) で `AxiosError.response.status`
 *   を参照して行う。
 */
export const updateMagazineCatch = async (
  titleName: string,
  body: MagazineCatchUpdateRequest
): Promise<MagazineCatchUpdateResponse> => {
  const parsedBody = MagazineCatchUpdateRequestSchema.parse(body);
  const response = await apiClient.post<unknown>(
    `/admin/magazines/${encodeURIComponent(titleName)}/catch`,
    parsedBody
  );
  return MagazineCatchUpdateResponseSchema.parse(response.data);
};
